import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowRight, HardHat, Mail, ShieldCheck } from "lucide-react";

export default function VendorLogin() {
  const nav = useNavigate();
  const [step, setStep] = useState("identify"); // identify | code
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [busy, setBusy] = useState(false);

  const request = async () => {
    if (!identifier.trim()) return toast.error("Enter your mobile number or email");
    setBusy(true);
    try {
      const { data } = await api.post("/fs/vendor-login/request", { identifier: identifier.trim() });
      setSentTo(data.sent_to || identifier.trim());
      setStep("code");
      toast.success("If we have you on file, a code is on its way");
    } catch (e) { toast.error(errMsg(e, "Could not send a code — try again")); }
    setBusy(false);
  };

  const verify = async () => {
    if (code.replace(/\D/g, "").length !== 6) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      const { data } = await api.post("/fs/vendor-login/verify", { identifier: identifier.trim(), code });
      toast.success("Signed in");
      nav(`/vendor/${data.token}`);
    } catch (e) { toast.error(errMsg(e, "That code didn't work")); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-sand-50 px-4 py-14" data-testid="vendor-login-page">
      <div className="mx-auto max-w-md">
        <div className="mb-6 flex items-center gap-2 text-ink">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-redline-500 text-white">
            <HardHat className="h-5 w-5" />
          </span>
          <span className="font-heading text-lg font-extrabold tracking-tight">Ask Redline · Vendors</span>
        </div>

        <div className="rounded-3xl bg-white p-7 shadow-sm">
          {step === "identify" ? (
            <>
              <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ink">Vendor sign-in</h1>
              <p className="mt-1.5 text-sm leading-relaxed text-ink/55">
                Enter the mobile number or email your company is set up with. We'll send you a
                6-digit code and a link straight to your vendor page — no password to remember.
              </p>
              <label className="mt-5 block text-xs font-bold uppercase tracking-wider text-ink/40">Mobile number or email</label>
              <input data-testid="vendor-login-identifier" value={identifier} autoFocus
                onChange={(e) => setIdentifier(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && request()}
                placeholder="you@company.com or (555) 123-4567"
                className="mt-1.5 w-full rounded-xl border border-sand-300 px-3.5 py-3 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
              <button data-testid="vendor-login-send" onClick={request} disabled={busy}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-redline-500 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-redline-600 disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send my code
              </button>
            </>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ink">Enter your code</h1>
              <p className="mt-1.5 text-sm leading-relaxed text-ink/55">
                We sent a 6-digit code to <span className="font-semibold text-ink/75">{sentTo}</span>.
                It expires in 15 minutes. You can also just tap the link in that message.
              </p>
              <input data-testid="vendor-login-code" value={code} autoFocus inputMode="numeric" maxLength={6}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => e.key === "Enter" && verify()}
                placeholder="123456"
                className="mt-5 w-full rounded-xl border border-sand-300 px-3.5 py-3 text-center text-2xl font-extrabold tracking-[0.4em] outline-none focus:ring-2 focus:ring-redline-500/30" />
              <button data-testid="vendor-login-verify" onClick={verify} disabled={busy}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-redline-500 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-redline-600 disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Open my vendor page
              </button>
              <div className="mt-3 flex items-center justify-between text-xs">
                <button data-testid="vendor-login-back" onClick={() => { setStep("identify"); setCode(""); }}
                  className="font-semibold text-ink/50 hover:text-ink/70">Use a different number/email</button>
                <button data-testid="vendor-login-resend" onClick={request} disabled={busy}
                  className="font-semibold text-redline-600 hover:text-redline-700 disabled:opacity-50">Resend code</button>
              </div>
            </>
          )}
        </div>

        <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs text-ink/40">
          <ShieldCheck className="h-3.5 w-3.5" /> Only companies already set up with a community can sign in.
        </p>
      </div>
    </div>
  );
}
